import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiArrowLeft,
  FiCheck, FiXCircle, FiEye, FiX, FiDollarSign, FiUserX, FiAlertTriangle, FiPrinter
} from 'react-icons/fi';
import { collection, getDocs, query, orderBy, where, doc, updateDoc } from 'firebase/firestore';
import PageLayout from '../../components/PageLayout';
import AccountSelect from '../../components/AccountSelect';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton,
  LoadingMessage, TranEmptyState, TableContainer, Table, Th, Tr, Td,
  StatusBadge, ActionGroup, IconBtn,
  FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea
} from '../../components/SharedStyles';
import {
  createTransaction, updateTransaction, deleteTransaction,
  getTransactions, updateTransactionStatus
} from '../../utils/transactionHelper';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import {
  loadFullOutstandingForStaff,
  applyFinalSettlementSideEffects,
  reverseFinalSettlementSideEffects
} from '../../utils/payrollHelper';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import useEscapeClose from '../../hooks/useEscapeClose';
import usePagePermissions from '../../hooks/usePagePermissions';
import PayMethodPicker from '../../components/PayMethodPicker';
import useCurrency from '../../hooks/useCurrency';
import useOpenFromReport from '../../hooks/useOpenFromReport';
import { formatAmount } from '../../utils/documentNumbering';

const TRAN_TYPE_CODE = 'FNF';
const STATUS_COLORS = {
  Draft: colors.warning.main,
  Posted: colors.info.main,
  Paid: colors.success.main,
  Cancelled: colors.error.main,
};

const LEAVING_REASONS = ['Resignation', 'Termination', 'Retirement', 'Mutual Separation', 'Other'];

// Default earnings rows pre-filled when staff is selected
const buildDefaultEarnings = () => ([
  { name: 'Last Month / Days Salary', amount: 0 },
  { name: 'Gratuity', amount: 0 },
  { name: 'Leave Encashment', amount: 0 },
  { name: 'Notice Pay', amount: 0 },
  { name: 'Bonus / Goodwill / Other', amount: 0 },
]);

const FinalSettlement = () => {
  const { activeCompany, activeFinancialYear, currentUser, activeBranch } = useAuth();
  const { canEdit, canDelete } = usePagePermissions('salary', 'finalSettlement');
  const { formatCurrency } = useCurrency();
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [viewMode, setViewMode] = useState('list');
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    docDate: new Date().toISOString().split('T')[0],
    partyId: '', partyName: '', partyAccountId: '', partyAccountCode: '',
    staffCode: '', designation: '', department: '',
    lastWorkingDay: new Date().toISOString().split('T')[0],
    leavingReason: 'Resignation',
    notes: '',
    status: 'Draft'
  });
  const [earnings, setEarnings] = useState(buildDefaultEarnings());
  const [deductions, setDeductions] = useState([]);
  const [loanRecovery, setLoanRecovery] = useState(0);
  const [loanRefs, setLoanRefs] = useState([]);
  const [advanceRecovery, setAdvanceRecovery] = useState(0);
  const [advanceRefs, setAdvanceRefs] = useState([]);

  const [staffList, setStaffList] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]); // Pay by cash (2026-10-09)
  const [payMethod, setPayMethod] = useState('bank'); // 'bank' | 'cash'
  const [showPayModal, setShowPayModal] = useState(false);
  useEscapeClose(showPayModal, () => setShowPayModal(false));
  const [payAccountId, setPayAccountId] = useState('');

  // Print config (per-company document format overrides)
  const [printConfig, setPrintConfig] = useState(null);
  useEffect(() => {
    if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig);
  }, [activeCompany]);

  const handlePrint = (fnf) => {
    printDocument('finalSettlement', fnf, activeCompany, printConfig, activeBranch);
  };

  // ─── Load data ───
  const loadSettlements = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const txs = await getTransactions(activeCompany.id, {
        tranTypeCode: TRAN_TYPE_CODE,
        ...(activeBranch && activeBranch.id !== 'all' ? { branchId: activeBranch.id } : {})
      });
      setSettlements(txs);
    } catch (e) { console.error('Load FNF error:', e); }
    finally { setLoading(false); }
  }, [activeCompany, activeBranch]);

  const loadLookups = useCallback(async () => {
    if (!activeCompany?.id) return;
    const [staffSnap, accSnap] = await Promise.all([
      // Load all staff, filter client-side (avoids needing a composite index on isActive+staffCode).
      // For new F&F we show only active; inactive (recently settled) shown in Edit mode by ID.
      getDocs(query(
        collection(db, 'companies', activeCompany.id, 'salaryStaff'),
        orderBy('staffCode')
      )),
      getDocs(collection(db, 'companies', activeCompany.id, 'accounts'))
    ]);
    setStaffList(
      staffSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(s => s.isActive !== false)
    );
    const allAccs = accSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    setBankAccounts(allAccs.filter(a => a.level === 5 && a.code?.startsWith('11102') && a.isActive !== false)
      .sort((a, b) => (a.code || '').localeCompare(b.code || '')));
    setCashAccounts(allAccs.filter(a => a.level === 5 && a.code?.startsWith('11101') && a.isActive !== false)
      .sort((a, b) => (a.code || '').localeCompare(b.code || '')));
  }, [activeCompany]);

  useEffect(() => { loadSettlements(); loadLookups(); }, [loadSettlements, loadLookups]);

  // ─── Form helpers ───
  const resetForm = () => {
    setEditing(null);
    setFormData({
      docDate: new Date().toISOString().split('T')[0],
      partyId: '', partyName: '', partyAccountId: '', partyAccountCode: '',
      staffCode: '', designation: '', department: '',
      lastWorkingDay: new Date().toISOString().split('T')[0],
      leavingReason: 'Resignation',
      notes: '',
      status: 'Draft'
    });
    setEarnings(buildDefaultEarnings());
    setDeductions([]);
    setLoanRecovery(0); setLoanRefs([]);
    setAdvanceRecovery(0); setAdvanceRefs([]);
  };

  const openNew = () => {
    if (!activeFinancialYear?.id) { alert('Activate a financial year first.'); return; }
    if (activeBranch && activeBranch.id === 'all') {
      alert('Pick a specific branch before creating a Final Settlement.');
      return;
    }
    resetForm();
    setViewMode('form');
  };

  const openEdit = (fnf) => {
    setEditing(fnf);
    setFormData({
      docDate: fnf.docDate || '',
      partyId: fnf.partyId || '',
      partyName: fnf.partyName || '',
      partyAccountId: fnf.partyAccountId || '',
      partyAccountCode: fnf.partyAccountCode || '',
      staffCode: fnf.staffCode || '',
      designation: fnf.designation || '',
      department: fnf.department || '',
      lastWorkingDay: fnf.lastWorkingDay || '',
      leavingReason: fnf.leavingReason || 'Resignation',
      notes: fnf.notes || '',
      status: fnf.status || 'Draft'
    });
    setEarnings(fnf.fnfEarnings || buildDefaultEarnings());
    setDeductions(fnf.fnfDeductions || []);
    setLoanRecovery(fnf.loanRecovery || 0);
    setLoanRefs(fnf.loanRefs || []);
    setAdvanceRecovery(fnf.advanceRecovery || 0);
    setAdvanceRefs(fnf.advanceRefs || []);
    setViewMode('form');
  };

  const goBack = () => { setViewMode('list'); resetForm(); };

  useOpenFromReport(settlements, openEdit);

  // ─── Staff selection: pre-fill structure + load outstanding ───
  const handleStaffChange = async (staffId) => {
    const s = staffList.find(x => x.id === staffId);
    if (!s) return;

    // Pre-fill last month salary as the staff's net (rough default; user adjusts)
    const lastSalaryDefault = Number(s.netSalary) || Number(s.basicSalary) || 0;
    const earningsInit = buildDefaultEarnings();
    earningsInit[0].amount = lastSalaryDefault;

    // Pre-fill statutory deductions from staff structure (full monthly amount)
    const dedInit = (s.deductions || []).map(d => {
      const computed = d.amountType === 'percent'
        ? (Number(s.basicSalary) || 0) * (Number(d.value) || 0) / 100
        : Number(d.value) || 0;
      return {
        name: d.name,
        amount: Math.round(computed * 100) / 100,
        accountId: d.accountId,
        accountCode: d.accountCode,
        accountName: d.accountName
      };
    });

    setFormData(prev => ({
      ...prev,
      partyId: staffId,
      partyName: s.fullName || '',
      partyAccountId: s.linkedAccountId || '',
      partyAccountCode: '',
      staffCode: s.staffCode || '',
      designation: s.designation || '',
      department: s.department || ''
    }));
    setEarnings(earningsInit);
    setDeductions(dedInit);

    // Load FULL outstanding loan + advance for recovery
    const outstanding = await loadFullOutstandingForStaff(activeCompany.id, staffId);
    setLoanRecovery(outstanding.loanTotal);
    setLoanRefs(outstanding.loanRefs);
    setAdvanceRecovery(outstanding.advanceTotal);
    setAdvanceRefs(outstanding.advanceRefs);
  };

  // ─── Earnings row handlers ───
  const updateEarning = (i, patch) => setEarnings(prev => prev.map((e, idx) => idx === i ? { ...e, ...patch } : e));
  const addEarning = () => setEarnings(prev => [...prev, { name: '', amount: 0 }]);
  const removeEarning = (i) => setEarnings(prev => prev.filter((_, idx) => idx !== i));

  // ─── Deduction row handlers ───
  const updateDeduction = (i, patch) => setDeductions(prev => prev.map((d, idx) => idx === i ? { ...d, ...patch } : d));
  const addDeduction = () => setDeductions(prev => [...prev, { name: '', amount: 0, accountId: '', accountCode: '', accountName: '' }]);
  const removeDeduction = (i) => setDeductions(prev => prev.filter((_, idx) => idx !== i));
  const handleDeductionAccount = (i, account) => updateDeduction(i, {
    accountId: account?.id || '',
    accountCode: account?.code || '',
    accountName: account?.name || ''
  });

  // ─── Computed totals ───
  const totals = useMemo(() => {
    const totalEarnings = earnings.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const totalStatutory = deductions.reduce((s, d) => s + (Number(d.amount) || 0), 0);
    const totalDeductions = totalStatutory + Number(loanRecovery) + Number(advanceRecovery);
    const netPayable = totalEarnings - totalDeductions;
    return {
      totalEarnings: Math.round(totalEarnings * 100) / 100,
      totalStatutory: Math.round(totalStatutory * 100) / 100,
      totalDeductions: Math.round(totalDeductions * 100) / 100,
      netPayable: Math.round(netPayable * 100) / 100
    };
  }, [earnings, deductions, loanRecovery, advanceRecovery]);

  // ─── Validation ───
  const validate = () => {
    if (!formData.partyId) return 'Select a Salary Staff member.';
    if (!formData.partyAccountId) return 'Selected staff has no linked COA account.';
    if (!formData.lastWorkingDay) return 'Last working day is required.';
    if (totals.totalEarnings <= 0) return 'Total earnings must be greater than zero.';
    if (totals.netPayable < 0) return `Net payable is negative (${formatCurrency(totals.netPayable)}). Reduce loan/advance recovery or increase earnings.`;
    for (let i = 0; i < deductions.length; i++) {
      const d = deductions[i];
      if (d.amount > 0 && !d.accountId) return `Deduction row ${i + 1} (${d.name || 'unnamed'}): pick a payable GL account.`;
    }
    return null;
  };

  const buildTransactionData = (status) => ({
    tranTypeCode: TRAN_TYPE_CODE,
    docDate: formData.docDate,
    financialYearId: activeFinancialYear?.id,
    branchId: activeBranch?.id || null,
    branchName: activeBranch?.branchName || null,
    partyType: 'staff',
    partyId: formData.partyId,
    partyName: formData.partyName,
    partyAccountId: formData.partyAccountId,
    partyAccountCode: formData.partyAccountCode,
    // F&F-specific
    staffId: formData.partyId,
    staffCode: formData.staffCode,
    designation: formData.designation,
    department: formData.department,
    lastWorkingDay: formData.lastWorkingDay,
    leavingReason: formData.leavingReason,
    fnfEarnings: earnings.map(e => ({ name: e.name.trim(), amount: Number(e.amount) || 0 })),
    fnfDeductions: deductions.map(d => ({
      name: d.name.trim(),
      amount: Number(d.amount) || 0,
      accountId: d.accountId,
      accountCode: d.accountCode,
      accountName: d.accountName
    })),
    loanRecovery: Number(loanRecovery) || 0,
    loanRefs,
    advanceRecovery: Number(advanceRecovery) || 0,
    advanceRefs,
    totalEarnings: totals.totalEarnings,
    totalStatutorySum: totals.totalStatutory,
    totalLoanRecoverySum: Number(loanRecovery) || 0,
    totalAdvanceRecoverySum: Number(advanceRecovery) || 0,
    totalDeductions: totals.totalDeductions,
    netPayable: totals.netPayable,
    notes: formData.notes,
    // Standard transaction-level totals
    subtotal: totals.totalEarnings,
    discountAmount: 0,
    taxableAmount: totals.totalEarnings,
    taxAmount: 0,
    whtRate: 0, whtAmount: 0,
    totalAmount: totals.netPayable,
    paidAmount: editing?.paidAmount || 0,
    balanceAmount: totals.netPayable - (editing?.paidAmount || 0),
    bulkPaymentTransactionId: editing?.bulkPaymentTransactionId || null,
    bulkPaymentDocNumber: editing?.bulkPaymentDocNumber || null,
    status
  });

  const handleSave = async (status = 'Draft') => {
    if (!canEdit) { alert('No permission.'); return; }
    const err = validate();
    if (err) { alert(err); return; }
    if (status === 'Posted') {
      if (!window.confirm(`Posting will: (1) close ALL outstanding loans & advances for ${formData.partyName}, (2) mark them as inactive (no future payroll), and (3) create the journal entries. Continue?`)) return;
    }

    setSaving(true);
    try {
      const data = buildTransactionData(status);

      // If editing a Posted F&F, reverse old side-effects first
      if (editing && editing.status === 'Posted') {
        await reverseFinalSettlementSideEffects(activeCompany.id, editing);
      }

      let saved;
      let newTranData = null;
      if (editing) {
        saved = await updateTransaction(activeCompany.id, editing.id, data, currentUser.uid);
      } else {
        newTranData = await createTransaction(activeCompany.id, data, currentUser.uid);
        saved = newTranData;
      }

      // Apply on Post
      if (status === 'Posted') {
        await applyFinalSettlementSideEffects(activeCompany.id, data, saved.id);
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedId = editing ? editing.id : newTranData?.id;
      const savedRecord = editing
        ? { ...editing, ...data, id: savedId, status }
        : { ...newTranData, status };
      setSettlements(prev => editing
        ? prev.map(r => r.id === savedId ? savedRecord : r)
        : [savedRecord, ...prev]);

      goBack();

      // Background refresh — reconciles with server data without blocking UI.
      loadSettlements().catch(err => console.error('Background refresh failed:', err));
    } catch (e) {
      console.error('Save FNF error:', e);
      alert('Failed: ' + (e?.message || ''));
    } finally { setSaving(false); }
  };

  const handleStatusChange = async (fnf, newStatus) => {
    if (!canEdit) { alert('No permission.'); return; }
    if (!window.confirm(`Change ${fnf.docNumber} status to "${newStatus}"?`)) return;
    try {
      if (newStatus === 'Cancelled' && (fnf.status === 'Posted' || fnf.status === 'Paid')) {
        await reverseFinalSettlementSideEffects(activeCompany.id, fnf);
      }
      await updateTransactionStatus(activeCompany.id, fnf.id, newStatus, currentUser.uid);
      await loadSettlements();
    } catch (e) { alert('Failed: ' + (e?.message || '')); }
  };

  const handleDelete = async (fnf) => {
    if (!canDelete) { alert('No permission.'); return; }
    if (!window.confirm(`Delete ${fnf.docNumber}? Reverses all closure side-effects (re-activates staff, restores loan/advance balances).`)) return;
    try {
      if (fnf.status === 'Posted' || fnf.status === 'Paid') {
        await reverseFinalSettlementSideEffects(activeCompany.id, fnf);
      }
      await deleteTransaction(activeCompany.id, fnf.id);
      await loadSettlements();
    } catch (e) { alert('Failed: ' + (e?.message || '')); }
  };

  // ─── Pay (single Bank Payment — or Cash Payment — for the net) ───
  const openPayModal = (fnf) => {
    if (!canEdit) { alert('No permission.'); return; }
    if (fnf.status !== 'Posted') { alert('Only Posted F&F can be paid.'); return; }
    setEditing(fnf);
    setPayMethod('bank');
    setPayAccountId(bankAccounts[0]?.id || '');
    setShowPayModal(true);
  };

  const choosePayMethod = (m) => {
    setPayMethod(m);
    setPayAccountId(((m === 'cash' ? cashAccounts : bankAccounts)[0] || {}).id || '');
  };

  const confirmPay = async () => {
    const isCash = payMethod === 'cash';
    if (!payAccountId) { alert(isCash ? 'Select a cash account.' : 'Select a bank account.'); return; }
    const bankAcc = (isCash ? cashAccounts : bankAccounts).find(b => b.id === payAccountId);
    if (!bankAcc) { alert(isCash ? 'Cash account not found.' : 'Bank account not found.'); return; }
    const fnf = editing;
    const net = Number(fnf.netPayable) || Number(fnf.totalAmount) || 0;
    if (net <= 0) { alert('Nothing to pay (net is zero or negative).'); return; }

    try {
      const subTransactionLines = [
        {
          accountId: fnf.partyAccountId,
          accountCode: fnf.partyAccountCode || null,
          accountName: fnf.partyName ? `Staff: ${fnf.partyName}` : 'Staff Account',
          debitAmount: Math.round(net * 100) / 100,
          creditAmount: 0,
          description: `F&F payment to ${fnf.partyName} (${fnf.docNumber})`
        },
        {
          accountId: bankAcc.id,
          accountCode: bankAcc.code,
          accountName: bankAcc.name,
          debitAmount: 0,
          creditAmount: Math.round(net * 100) / 100,
          description: `F&F disbursement ${fnf.docNumber}`
        }
      ];

      const bp = await createTransaction(activeCompany.id, {
        tranTypeCode: isCash ? 'CP' : 'BP',
        docDate: new Date().toISOString().split('T')[0],
        financialYearId: fnf.financialYearId,
        branchId: fnf.branchId, branchName: fnf.branchName,
        partyType: null, partyId: null, partyName: `F&F: ${fnf.partyName}`,
        ...(isCash
          ? { cashAccountId: bankAcc.id, cashAccountCode: bankAcc.code, cashAccountName: bankAcc.name }
          : { bankAccountId: bankAcc.id, bankAccountCode: bankAcc.code, bankAccountName: bankAcc.name }),
        subtotal: net, totalAmount: net, balanceAmount: 0,
        notes: `Final Settlement disbursement for ${fnf.docNumber}`,
        status: 'Posted',
        finalSettlementId: fnf.id,
        finalSettlementDocNumber: fnf.docNumber,
        subTransactionLines
      }, currentUser.uid);

      await updateDoc(doc(db, 'companies', activeCompany.id, 'transactions', fnf.id), {
        paidAmount: net,
        balanceAmount: 0,
        bulkPaymentTransactionId: bp.id,
        bulkPaymentDocNumber: bp.docNumber,
        bulkPaymentType: isCash ? 'CP' : 'BP',
        status: 'Paid',
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid
      });

      setShowPayModal(false);
      await loadSettlements();
      alert(`F&F net Rs ${formatAmount(net)} paid via ${isCash ? 'Cash' : 'Bank'} Payment ${bp.docNumber}.`);
    } catch (e) {
      console.error('Pay FNF error:', e);
      alert('Failed: ' + (e?.message || ''));
    }
  };

  useDocumentShortcuts({
    onNew: openNew,
    onSave: () => handleSave(formData.status === 'Posted' ? 'Posted' : 'Draft'),
    viewMode,
    saving
  });

  const filtered = settlements.filter(s => {
    const m = !searchQuery ||
      s.docNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.partyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.staffCode?.toLowerCase().includes(searchQuery.toLowerCase());
    const st = statusFilter === 'All' || s.status === statusFilter;
    return m && st;
  });

  // ═══════════════════════════════════════════════════════════ LIST
  if (viewMode === 'list') {
    return (
      <PageLayout pageTitle="Final Settlement (F&F)" breadcrumb="Salary / Final Settlement">
        <ActionBar>
          <SearchBox>
            <FiSearch />
            <input type="text" placeholder="Search doc#, staff name or code..."
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          </SearchBox>
          <FilterGroup>
            <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="All">All Status</option>
              {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
            </StatusFilterSelect>
          </FilterGroup>
          {canEdit && <TranAddButton onClick={openNew}><FiPlus /> New Final Settlement</TranAddButton>}
        </ActionBar>

        {loading ? <LoadingMessage>Loading...</LoadingMessage>
          : filtered.length === 0 ? (
            <TranEmptyState>
              {searchQuery || statusFilter !== 'All'
                ? 'No settlements match your filters.'
                : 'No final settlements yet. Click "New Final Settlement" when a staff member leaves.'}
            </TranEmptyState>
          ) : (
            <TableContainer>
              <Table>
                <thead><tr>
                  <Th>Doc #</Th><Th>Date</Th><Th>Staff</Th><Th>Last Day</Th><Th>Reason</Th>
                  <Th style={{ textAlign: 'right' }}>Earnings</Th>
                  <Th style={{ textAlign: 'right' }}>Loan Closed</Th>
                  <Th style={{ textAlign: 'right' }}>Advance Closed</Th>
                  <Th style={{ textAlign: 'right' }}>Net Paid</Th>
                  {showBranchCol && <Th>Branch</Th>}
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr></thead>
                <tbody>
                  {filtered.map(f => (
                    <Tr key={f.id}>
                      <Td><strong>{f.docNumber}</strong></Td>
                      <Td>{f.docDate}</Td>
                      <Td>
                        <div>{f.partyName}</div>
                        <Sub>{f.staffCode}{f.designation ? ` · ${f.designation}` : ''}</Sub>
                      </Td>
                      <Td>{f.lastWorkingDay || '-'}</Td>
                      <Td><ReasonBadge>{f.leavingReason || '-'}</ReasonBadge></Td>
                      <Td style={{ textAlign: 'right' }}>{formatCurrency(f.totalEarnings || 0)}</Td>
                      <Td style={{ textAlign: 'right', color: colors.warning.dark }}>−{formatCurrency(f.totalLoanRecoverySum || 0)}</Td>
                      <Td style={{ textAlign: 'right', color: colors.warning.dark }}>−{formatCurrency(f.totalAdvanceRecoverySum || 0)}</Td>
                      <Td style={{ textAlign: 'right', fontWeight: 700 }}>{formatCurrency(f.netPayable || 0)}</Td>
                      {showBranchCol && <Td>{f.branchName || '-'}</Td>}
                      <Td>
                        <StatusBadge style={{ background: STATUS_COLORS[f.status] + '20', color: STATUS_COLORS[f.status] }}>
                          {f.status}
                        </StatusBadge>
                      </Td>
                      <Td>
                        <ActionGroup>
                          <IconBtn title="View / Edit" onClick={() => openEdit(f)}><FiEye /></IconBtn>
                          <IconBtn title="Print" onClick={() => handlePrint(f)}><FiPrinter /></IconBtn>
                          {canEdit && f.status === 'Draft' && (
                            <IconBtn title="Post Settlement" onClick={() => handleStatusChange(f, 'Posted')}><FiCheck /></IconBtn>
                          )}
                          {canEdit && f.status === 'Posted' && (
                            <IconBtn title="Pay Net (Bank or Cash Payment)" onClick={() => openPayModal(f)}><FiDollarSign /></IconBtn>
                          )}
                          {canEdit && (f.status === 'Posted' || f.status === 'Paid') && (
                            <IconBtn title="Cancel (re-activates staff, restores balances)" onClick={() => handleStatusChange(f, 'Cancelled')}><FiXCircle /></IconBtn>
                          )}
                          {canDelete && (
                            <IconBtn $danger title="Delete" onClick={() => handleDelete(f)}><FiTrash2 /></IconBtn>
                          )}
                        </ActionGroup>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          )}

        {/* Pay modal */}
        {showPayModal && (
          <Overlay onClick={() => setShowPayModal(false)}>
            <PayModal onClick={e => e.stopPropagation()}>
              <ModalH>
                <h3>Pay F&F — {editing?.docNumber}</h3>
                <CloseBtn onClick={() => setShowPayModal(false)}><FiX /></CloseBtn>
              </ModalH>
              <ModalB>
                <PayMethodPicker value={payMethod} onChange={choosePayMethod} />
                <p style={{ marginBottom: spacing.md }}>
                  Creates a {payMethod === 'cash' ? 'Cash' : 'Bank'} Payment of <strong>{formatCurrency(editing?.netPayable || 0)}</strong> to <strong>{editing?.partyName}</strong>.
                </p>
                <FormGroup>
                  <Label>{payMethod === 'cash' ? 'Pay From Cash Account *' : 'Pay From Bank Account *'}</Label>
                  <Select value={payAccountId} onChange={e => setPayAccountId(e.target.value)}>
                    <option value="">— Select —</option>
                    {(payMethod === 'cash' ? cashAccounts : bankAccounts).map(b => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
                  </Select>
                </FormGroup>
              </ModalB>
              <ModalF>
                <CancelBtn onClick={() => setShowPayModal(false)}>Cancel</CancelBtn>
                <ConfirmBtn onClick={confirmPay}>Confirm & Disburse</ConfirmBtn>
              </ModalF>
            </PayModal>
          </Overlay>
        )}
      </PageLayout>
    );
  }

  // ═══════════════════════════════════════════════════════════ FORM
  const isEditable = !editing || editing.status === 'Draft';
  const negative = totals.netPayable < 0;

  return (
    <PageLayout pageTitle="Final Settlement (F&F)" breadcrumb="Salary / Final Settlement">
      <FormHeader>
        <BackButton onClick={goBack}><FiArrowLeft /> Back</BackButton>
        <FormTitle>
          {editing ? `Edit ${editing.docNumber}` : 'New Final Settlement'}
          {formData.partyName && ` — ${formData.partyName}`}
        </FormTitle>
        <FormActions>
          {editing && (
            <SaveButton type="button" onClick={() => handlePrint(editing)}
              style={{ background: colors.text.secondary }}>
              <FiPrinter /> Print
            </SaveButton>
          )}
          {isEditable && (
            <SaveButton type="button" onClick={() => handleSave('Draft')} disabled={saving}>
              Save Draft
            </SaveButton>
          )}
          {isEditable && (
            <SaveButton type="button" onClick={() => handleSave('Posted')} disabled={saving}
              style={{ background: colors.error.main }}>
              {saving ? 'Saving...' : 'Save & Post (close staff)'}
            </SaveButton>
          )}
        </FormActions>
      </FormHeader>

      {/* Section A: Staff & Reason */}
      <FormCard>
        <SectionTitle>Staff & Departure</SectionTitle>
        <FormGrid>
          <FormGroup>
            <Label>Doc Date *</Label>
            <Input type="date" value={formData.docDate}
              onChange={e => setFormData({...formData, docDate: e.target.value})}
              disabled={!isEditable} />
          </FormGroup>
          <FormGroup>
            <Label>Salary Staff *</Label>
            <Select value={formData.partyId} onChange={e => handleStaffChange(e.target.value)}
              disabled={!isEditable || !!editing}>
              <option value="">— Select Staff —</option>
              {/* In edit mode, also include the previously-selected (now inactive) staff */}
              {editing && !staffList.find(s => s.id === editing.partyId) && (
                <option value={editing.partyId}>{editing.staffCode} — {editing.partyName} (inactive)</option>
              )}
              {staffList.map(s => (
                <option key={s.id} value={s.id}>{s.staffCode} — {s.fullName}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Last Working Day *</Label>
            <Input type="date" value={formData.lastWorkingDay}
              onChange={e => setFormData({...formData, lastWorkingDay: e.target.value})}
              disabled={!isEditable} />
          </FormGroup>
          <FormGroup>
            <Label>Reason</Label>
            <Select value={formData.leavingReason}
              onChange={e => setFormData({...formData, leavingReason: e.target.value})}
              disabled={!isEditable}>
              {LEAVING_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </Select>
          </FormGroup>
          {formData.staffCode && (
            <FormGroup style={{ gridColumn: 'span 2' }}>
              <Label>&nbsp;</Label>
              <StaffInfo>
                <span><strong>{formData.staffCode}</strong></span>
                {formData.designation && <span>{formData.designation}</span>}
                {formData.department && <span>{formData.department}</span>}
              </StaffInfo>
            </FormGroup>
          )}
        </FormGrid>
      </FormCard>

      {/* Section B: Earnings */}
      <FormCard>
        <RowHeader>
          <SectionTitle style={{ margin: 0 }}>Earnings</SectionTitle>
          {isEditable && <SmallBtn onClick={addEarning}><FiPlus /> Add</SmallBtn>}
        </RowHeader>
        <ItemTable>
          <thead><tr>
            <Th2 style={{ width: 36 }}>#</Th2>
            <Th2>Description</Th2>
            <Th2 style={{ width: 200, textAlign: 'right' }}>Amount</Th2>
            <Th2 style={{ width: 40 }}></Th2>
          </tr></thead>
          <tbody>
            {earnings.map((e, i) => (
              <tr key={i}>
                <Td2>{i + 1}</Td2>
                <Td2>
                  <Inp value={e.name} onChange={ev => updateEarning(i, { name: ev.target.value })}
                    disabled={!isEditable} />
                </Td2>
                <Td2>
                  <Inp type="number" min="0" step="0.01" value={e.amount}
                    onChange={ev => updateEarning(i, { amount: Number(ev.target.value) || 0 })}
                    disabled={!isEditable} style={{ textAlign: 'right' }} />
                </Td2>
                <Td2 style={{ textAlign: 'center' }}>
                  {isEditable && <RmBtn onClick={() => removeEarning(i)}><FiX /></RmBtn>}
                </Td2>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <FootRow>
              <td colSpan="2" style={{ textAlign: 'right' }}><strong>TOTAL EARNINGS:</strong></td>
              <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: colors.success.dark }}>
                {formatAmount(totals.totalEarnings)}
              </td>
              <td></td>
            </FootRow>
          </tfoot>
        </ItemTable>
      </FormCard>

      {/* Section C: Outstanding (Auto-pulled, editable) */}
      {(loanRecovery > 0 || advanceRecovery > 0 || loanRefs.length > 0 || advanceRefs.length > 0) && (
        <FormCard>
          <SectionTitle>Outstanding Recoveries (auto-pulled)</SectionTitle>
          <RecoveryGrid>
            <RecCard>
              <RecHeader>
                <span>Loan Outstanding</span>
                <small>{loanRefs.length} loan(s)</small>
              </RecHeader>
              <Inp type="number" min="0" step="0.01" value={loanRecovery}
                onChange={e => setLoanRecovery(Number(e.target.value) || 0)}
                disabled={!isEditable}
                style={{ textAlign: 'right', fontSize: 18, fontWeight: 700 }} />
              {loanRefs.length > 0 && (
                <RefList>{loanRefs.map(r => `${r.docNumber} (${formatCurrency(r.amount)})`).join(', ')}</RefList>
              )}
            </RecCard>
            <RecCard>
              <RecHeader>
                <span>Advance Outstanding</span>
                <small>{advanceRefs.length} advance(s)</small>
              </RecHeader>
              <Inp type="number" min="0" step="0.01" value={advanceRecovery}
                onChange={e => setAdvanceRecovery(Number(e.target.value) || 0)}
                disabled={!isEditable}
                style={{ textAlign: 'right', fontSize: 18, fontWeight: 700 }} />
              {advanceRefs.length > 0 && (
                <RefList>{advanceRefs.map(r => `${r.docNumber} (${formatCurrency(r.amount)})`).join(', ')}</RefList>
              )}
            </RecCard>
          </RecoveryGrid>
        </FormCard>
      )}

      {/* Section D: Statutory + Custom Deductions */}
      <FormCard>
        <RowHeader>
          <SectionTitle style={{ margin: 0 }}>Statutory & Other Deductions</SectionTitle>
          {isEditable && <SmallBtn onClick={addDeduction}><FiPlus /> Add</SmallBtn>}
        </RowHeader>
        {deductions.length === 0 ? (
          <EmptyHint>No deductions. Click Add for income tax, EOBI, or custom recoveries.</EmptyHint>
        ) : (
          <ItemTable>
            <thead><tr>
              <Th2 style={{ width: 36 }}>#</Th2>
              <Th2>Name</Th2>
              <Th2 style={{ width: 160, textAlign: 'right' }}>Amount</Th2>
              <Th2 style={{ minWidth: 220 }}>Payable Account (Cr)</Th2>
              <Th2 style={{ width: 40 }}></Th2>
            </tr></thead>
            <tbody>
              {deductions.map((d, i) => (
                <tr key={i}>
                  <Td2>{i + 1}</Td2>
                  <Td2>
                    <Inp value={d.name} onChange={ev => updateDeduction(i, { name: ev.target.value })}
                      disabled={!isEditable} />
                  </Td2>
                  <Td2>
                    <Inp type="number" min="0" step="0.01" value={d.amount}
                      onChange={ev => updateDeduction(i, { amount: Number(ev.target.value) || 0 })}
                      disabled={!isEditable} style={{ textAlign: 'right' }} />
                  </Td2>
                  <NoClipTd>
                    <AccountSelect
                      companyId={activeCompany?.id}
                      value={d.accountId}
                      onChange={(account) => handleDeductionAccount(i, account)}
                      label="Payable A/c"
                      disabled={!isEditable}
                    />
                  </NoClipTd>
                  <Td2 style={{ textAlign: 'center' }}>
                    {isEditable && <RmBtn onClick={() => removeDeduction(i)}><FiX /></RmBtn>}
                  </Td2>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <FootRow>
                <td colSpan="2" style={{ textAlign: 'right' }}><strong>STATUTORY TOTAL:</strong></td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: colors.warning.dark }}>
                  {formatAmount(totals.totalStatutory)}
                </td>
                <td colSpan="2"></td>
              </FootRow>
            </tfoot>
          </ItemTable>
        )}
      </FormCard>

      {/* Section E: Final summary */}
      <FormCard>
        <SectionTitle>Settlement Summary</SectionTitle>
        <SummaryGrid>
          <SumItem><span>Total Earnings</span><strong style={{ color: colors.success.dark }}>{formatCurrency(totals.totalEarnings)}</strong></SumItem>
          <SumItem><span>− Statutory</span><strong>−{formatCurrency(totals.totalStatutory)}</strong></SumItem>
          <SumItem><span>− Loan Recovery</span><strong>−{formatCurrency(loanRecovery)}</strong></SumItem>
          <SumItem><span>− Advance Recovery</span><strong>−{formatCurrency(advanceRecovery)}</strong></SumItem>
          <SumItem $highlight $negative={negative}>
            <span>NET PAYABLE</span>
            <strong>{formatCurrency(totals.netPayable)}</strong>
          </SumItem>
        </SummaryGrid>
        {negative && (
          <NegativeWarn>
            <FiAlertTriangle /> Net is negative — staff owes the company on departure.
            Reduce loan/advance recovery if you want to write off any portion, or increase earnings (e.g. add more notice pay).
          </NegativeWarn>
        )}
      </FormCard>

      <FormCard>
        <FormGroup>
          <Label>Notes (printed on F&F summary)</Label>
          <TextArea rows={2} value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})}
            disabled={!isEditable} />
        </FormGroup>
      </FormCard>

      <InfoNote>
        <strong>What happens on Post:</strong>
        <ul>
          <li><strong>Closes all outstanding loans</strong> for this staff (status → Closed) and recovers them in full from earnings.</li>
          <li><strong>Closes all outstanding advances</strong> (status → Recovered).</li>
          <li><strong>Marks staff as inactive</strong> — they won't appear in future Payroll Runs but their history is preserved.</li>
          <li>Posts the journal: <code>Dr Salaries Expense / Cr Staff Account / Cr each deduction account</code>.</li>
          <li>After Post, click <strong>Pay</strong> on the list to disburse the net via Bank Payment.</li>
        </ul>
      </InfoNote>
    </PageLayout>
  );
};

// ─── Local Styles ───
const Sub = styled.div`font-size: 11px; color: ${colors.text.tertiary}; margin-top: 2px;`;
const ReasonBadge = styled.span`
  display: inline-block; padding: 2px 8px;
  background: ${colors.warning.main}15; color: ${colors.warning.dark};
  border-radius: ${borderRadius.sm}; font-size: 11px; font-weight: 600;
`;
const StaffInfo = styled.div`
  display: flex; gap: ${spacing.lg}; align-items: center;
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.neutral[50]}; border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
  span { color: ${colors.text.secondary}; }
  strong { color: ${colors.primary.main}; }
`;

const RowHeader = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: ${spacing.sm};`;
const SmallBtn = styled.button`
  display: flex; align-items: center; gap: 4px; padding: 4px 10px;
  background: ${colors.primary.main}; color: white; border: none;
  border-radius: ${borderRadius.sm}; font-size: 11px; font-weight: 600; cursor: pointer;
  &:hover { background: ${colors.primary.dark}; }
`;
const ItemTable = styled.table`width: 100%; border-collapse: collapse; border: 1px solid ${colors.border.light}; border-radius: ${borderRadius.sm};`;
const Th2 = styled.th`
  padding: 8px; background: ${colors.neutral[50]}; color: ${colors.text.secondary};
  font-size: 11px; font-weight: 600; text-transform: uppercase; text-align: left;
  border-bottom: 1px solid ${colors.border.light};
`;
const Td2 = styled.td`padding: 4px 8px; font-size: ${typography.fontSize.sm}; border-bottom: 1px solid ${colors.neutral[100]}; vertical-align: middle;`;
const NoClipTd = styled(Td2)`overflow: visible; position: relative; z-index: 1;`;
const Inp = styled.input`
  width: 100%; padding: 6px 8px;
  border: 1px solid ${colors.border.light}; border-radius: 3px;
  font-size: ${typography.fontSize.sm};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
  &:disabled { background: ${colors.neutral[100]}; color: ${colors.text.tertiary}; }
`;
const RmBtn = styled.button`
  width: 26px; height: 26px;
  background: transparent; border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm}; color: ${colors.error.main}; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  &:hover { background: ${colors.error.main}; color: white; border-color: transparent; }
`;
const FootRow = styled.tr`
  background: ${colors.neutral[100]};
  td { padding: 8px; border-top: 2px solid ${colors.border.light}; font-size: ${typography.fontSize.sm}; }
`;
const EmptyHint = styled.div`
  padding: ${spacing.md}; text-align: center;
  border: 1px dashed ${colors.border.light}; border-radius: ${borderRadius.sm};
  color: ${colors.text.secondary}; font-size: ${typography.fontSize.sm};
`;

const RecoveryGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${spacing.md};
`;
const RecCard = styled.div`
  padding: ${spacing.md};
  background: ${colors.warning.main}10;
  border: 1px solid ${colors.warning.main}30;
  border-radius: ${borderRadius.sm};
`;
const RecHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: ${spacing.sm};
  span { font-weight: 600; color: ${colors.text.primary}; }
  small { color: ${colors.text.secondary}; font-size: 11px; }
`;
const RefList = styled.div`
  margin-top: ${spacing.sm}; padding: ${spacing.sm};
  background: white; border-radius: ${borderRadius.sm};
  font-size: 11px; color: ${colors.text.secondary};
`;

const SummaryGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${spacing.md};
`;
const SumItem = styled.div`
  padding: ${spacing.md};
  background: ${props => props.$highlight ? (props.$negative ? colors.error.main : colors.primary.main) : colors.neutral[50]};
  color: ${props => props.$highlight ? 'white' : colors.text.primary};
  border-radius: ${borderRadius.sm};
  display: flex; flex-direction: column; gap: 4px;
  span { font-size: 11px; opacity: 0.85; text-transform: uppercase; letter-spacing: 0.5px; }
  strong { font-size: ${typography.fontSize.lg}; font-family: monospace; }
`;
const NegativeWarn = styled.div`
  margin-top: ${spacing.md};
  padding: ${spacing.md};
  background: ${colors.error.main}15;
  border-left: 4px solid ${colors.error.main};
  border-radius: ${borderRadius.sm};
  color: ${colors.error.dark};
  display: flex; gap: ${spacing.sm}; align-items: flex-start;
  font-size: ${typography.fontSize.sm}; line-height: 1.5;
  svg { flex-shrink: 0; margin-top: 2px; }
`;

const InfoNote = styled.div`
  margin-top: ${spacing.md};
  padding: ${spacing.md};
  background: ${colors.info.main}10;
  border-left: 3px solid ${colors.info.main};
  border-radius: ${borderRadius.sm};
  color: ${colors.text.secondary}; font-size: 12px; line-height: 1.6;
  ul { margin: 6px 0 0 ${spacing.lg}; padding: 0; }
  li { margin: 3px 0; }
  code { background: white; padding: 1px 6px; border-radius: 3px; font-family: monospace; color: ${colors.primary.main}; }
`;

// Pay modal — match Banks/Customers master modal style
const Overlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 20px;
`;
const PayModal = styled.div`
  background: white; border-radius: ${borderRadius.lg};
  width: 100%; max-width: 500px;
  max-height: 92vh; overflow-y: auto;
  box-shadow: ${shadows.xl};
`;
const ModalH = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 20px 24px; border-bottom: 1px solid ${colors.border.light};
  h3 {
    font-size: 18px; font-weight: 600; color: ${colors.text.primary}; margin: 0;
    display: flex; align-items: center; gap: 10px;
  }
`;
const ModalB = styled.div`padding: 24px;`;
const ModalF = styled.div`
  padding: 20px 24px; border-top: 1px solid ${colors.border.light};
  display: flex; justify-content: flex-end; gap: 12px;
`;
const CloseBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px;
  background: transparent; border: none;
  color: ${colors.text.tertiary}; cursor: pointer;
  border-radius: ${borderRadius.sm};
  &:hover { background: ${colors.background.paper}; color: ${colors.text.primary}; }
`;
const CancelBtn = styled.button`
  padding: 10px 20px;
  background: white; color: ${colors.text.secondary};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  font-size: 14px; font-weight: 500;
  cursor: pointer; transition: all 0.2s;
  &:hover { background: ${colors.background.paper}; }
`;
const ConfirmBtn = styled.button`
  display: flex; align-items: center; gap: 8px;
  padding: 10px 20px;
  background: ${colors.primary.main}; color: white;
  border: none; border-radius: ${borderRadius.md};
  font-size: 14px; font-weight: 500;
  cursor: pointer; transition: all 0.2s;
  &:hover:not(:disabled) { background: ${colors.primary.dark}; }
`;

export default FinalSettlement;
